import React from 'react';
import { withRouter } from 'react-router-dom';
import get from 'lodash/get';
import {
  Paper, Button, TextField, Grid, InputLabel,
} from '@mui/material';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { isEqual } from 'lodash';

import SmallMap from '../common/small-map';
import ErrorMessage from '../common/error-message';
import withPersistentSettings from '../common/table-with-persistent-settings';

import marketActions from '../../actions/markets';
import * as snackbarActions from '../../actions/snackbar';
import featureFlagsActions from '../../actions/featureFlags';
import { shouldFetchList, FeatureFlagTypes } from '../../lib/utils';
import TargetDeploymentContainer from './target-deployments';
import { FeatureFlagsModal } from './featureFlagsModal';

const styles = {
  root: {
    width: '95%',
    marginLeft: 'auto',
    marginRight: 'auto',
    overflowX: 'auto',
  },
  rowSpace: {
    marginTop: '15px',
  },
  floatRight: {
    float: 'right',
  },
};

class MarketEditPage extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      market: props.selectedMarket,
      error: false,
      showTargetDeploymentTable: false,
      showFeatureFlagsModal: false,
      editFeatureFlags: [],
    };

    this.handleSave = this.handleSave.bind(this);
    this.handleClose = this.handleClose.bind(this);
  }

  componentDidMount() {
    const {
      selectedMarket, markets, selectMarket, fetchMarkets, match,
      featureFlags, fetchFeatureFlags,
    } = this.props;
    // fire the market fetch
    if (!markets) {
      fetchMarkets();
    }

    // fire the select
    if (markets && markets.length && !selectedMarket) {
      const targetMarketId = get(match, 'params.marketId');
      if (targetMarketId) {
        const targetMarket = markets.find((market) => market._id === targetMarketId);
        selectMarket(targetMarket);
      }
    }
    const { marketId } = get(this, 'props.match.params');
    if (marketId) {
      if (shouldFetchList(marketId, featureFlags)) fetchFeatureFlags(marketId);
    }
  }

  componentDidUpdate(prevProps) {
    const {
      selectedMarket, markets, selectMarket, fetchMarkets, match,
    } = this.props;
    const { market: stateMarket } = this.state;

    // fire the market fetch
    if (!markets && markets !== prevProps.markets) {
      fetchMarkets();
    }

    // fire the select
    if (markets
        && markets.length
        && !selectedMarket
        && selectedMarket !== prevProps.selectedMarket) {
      const targetMarketId = get(match, 'params.marketId');
      if (targetMarketId) {
        const targetMarket = markets.find((market) => market._id === targetMarketId);
        selectMarket(targetMarket);
      }
    }

    // set local market state; check sub-arrays as these will fail a (shallow, mutable) comparison
    if (!stateMarket && selectedMarket) {
      this.setState({ market: selectedMarket });
    }
  }

  get coordinates() {
    const { market } = this.state;
    const coords = get(market, 'geo.coordinates');
    if (coords && coords.length >= 2) {
      return { lat: coords[1], lng: coords[0] };
    }
    return null;
  }

  handleSave(
    flags,
    lowBatteryThreshold,
    marketVanCapacity,
    marketDeploymentDuration,
    marketRebalanceDuration,
    hoursToExpireRun,
    hoursToCloseRun,
    alerts,
  ) {
    const { showSuccessSnackbar, showErrorSnackbar, updateMarket } = this.props;
    const { marketId } = get(this, 'props.match.params');
    const { selectedMarket } = this.props;

    let errorMessage = '';

    if (flags.length > 0
      || !isEqual(selectedMarket.alerts, alerts)
      || selectedMarket.lowBatteryThreshold !== lowBatteryThreshold
      || selectedMarket.marketVanCapacity !== marketVanCapacity
      || selectedMarket.marketDeploymentDuration !== marketDeploymentDuration
      || selectedMarket.marketRebalanceDuration !== marketRebalanceDuration
      || selectedMarket.hoursToExpireRun !== hoursToExpireRun
      || selectedMarket.hoursToCloseRun !== hoursToCloseRun
    ) {
      this.props.bulkUpdateFlagStatus(flags)
        .then((data) => {
          const errors = get(data, 'data.failures', []);
          const statusCode = get(data, 'status');
          const errorFlags = errors.map((err) => err);
          if (statusCode !== 200 && statusCode !== 201) {
            errorMessage += 'Server error while updating feature flag(s). ';
            showErrorSnackbar('Server error while updating feature flag(s)');
          } else if (errors && errors.length > 0) {
            errorMessage += `Error updating feature flag(s): ${errorFlags}. `;
            showErrorSnackbar(`Error updating feature flag(s): ${errorFlags}`);
          }
        })
        .then(() => {
          if (errorMessage === '') {
            updateMarket({
              ...selectedMarket,
              lowBatteryThreshold,
              alerts,
              marketDeploymentDuration,
              marketRebalanceDuration,
              marketVanCapacity,
              hoursToExpireRun,
              hoursToCloseRun,
            }, marketId)
              .then((data) => {
                const hasError = get(data, 'hasError', false);
                const errorMsg = get(data, 'errorObject.message', '');
                if (hasError) {
                  errorMessage += errorMsg;
                  showErrorSnackbar(errorMessage);
                } else {
                  showSuccessSnackbar('Successfully updated feature flag(s)');
                }
              });
          }
          this.setState({
            showFeatureFlagsModal: false,
            editFeatureFlags: false,
          });
        })
        .then(() => this.props.fetchMarkets())
        .then(() => this.props.fetchFeatureFlags(marketId))
        .catch((error) => {
          showErrorSnackbar(`Error from server: ${error}`);
          this.setState({
            showFeatureFlagsModal: false,
          });
          this.props.fetchMarkets();
          this.props.fetchFeatureFlags(marketId);
        });
    } else {
      showSuccessSnackbar('Successfully updated 0 feature flags');
      this.setState({
        showFeatureFlagsModal: false,
        editFeatureFlags: false,
      });
    }
  }

  handleClose() {
    const { marketId } = get(this, 'props.match.params');
    if (marketId) {
      this.props.fetchFeatureFlags(marketId);
    }

    this.setState({
      showFeatureFlagsModal: false,
      editFeatureFlags: [],
    });
  }

  render() {
    const {
      editing, featureFlags, selectedMarket, showErrorSnackbar,
    } = this.props;
    const {
      error,
      market,
      errorMessage,
      showTargetDeploymentTable,
      showFeatureFlagsModal,
      editFeatureFlags,
    } = this.state;
    const marketflags = featureFlags || [];
    const flagsByType = marketflags.filter(
      (flag) => flag.type === FeatureFlagTypes.OOOZ_NOTIFICATION
      || flag.type === FeatureFlagTypes.OOOZ_TASK
      || flag.type === FeatureFlagTypes.OOOZ_CASE
      || flag.type === FeatureFlagTypes.BEACON_NOTIFICATION
      || flag.type === FeatureFlagTypes.BEACON_TASK
      || flag.type === FeatureFlagTypes.BEACON_CASE
      || flag.type === FeatureFlagTypes.IDLE_VEHICLE_TASK
      || flag.type === FeatureFlagTypes.IDLE_VEHICLE_CASE
      || flag.type === FeatureFlagTypes.IDLE_VEHICLE_NOTIFICATION
      || flag.type === FeatureFlagTypes.LOW_BATTERY_THRESHOLD
      || flag.type === FeatureFlagTypes.LOW_BATTERY_TASK,
    );
    return (
      <Paper style={styles.root} sx={{ mt: 3 }}>
        {!showTargetDeploymentTable
        && (
          <div>
            {error
            && (
              <div style={styles.rowSpace}>
                <ErrorMessage
                  errorMessage={errorMessage}
                />
              </div>
            )}
            <div style={styles.floatRight}>
              {editing
              && (
                <Button
                  onClick={() => this.setState(
                    {
                      showFeatureFlagsModal: true,
                      editFeatureFlags: flagsByType,
                    },
                  )}
                  color="secondary"
                  variant="outlined"
                >
                  Automation Settings
                </Button>
              )}
              {editing
              && (
                <Button
                  onClick={() => this.setState({ showTargetDeploymentTable: true })}
                  color="secondary"
                  variant="outlined"
                >
                  Target Deployment
                </Button>
              )}
            </div>
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <InputLabel>
                  Approximate Center
                </InputLabel>
                <SmallMap
                  center={this.coordinates}
                />
              </Grid>
              <Grid item xs={6} style={styles.rowSpace}>
                <InputLabel>
                  Name
                </InputLabel>
                <TextField
                  autoFocus
                  margin="dense"
                  name="name"
                  type="text"
                  variant="standard"
                  value={get(market, 'name', '')}
                  fullWidth
                  disabled
                />
                <InputLabel>
                  Canonical Name
                </InputLabel>
                <TextField
                  autoFocus
                  margin="dense"
                  name="canonicalName"
                  type="text"
                  variant="standard"
                  value={get(market, 'canonicalName', '')}
                  fullWidth
                  disabled
                />
                <div style={styles.rowSpace}>
                  <InputLabel>
                    Time Zone
                  </InputLabel>
                  <TextField
                    autoFocus
                    margin="dense"
                    name="tz"
                    type="text"
                    variant="standard"
                    value={get(market, 'tz', '')}
                    fullWidth
                    disabled
                  />
                </div>
                <div style={styles.rowSpace}>
                  <InputLabel>
                    Status
                  </InputLabel>
                  <TextField
                    autoFocus
                    margin="dense"
                    name="status"
                    type="text"
                    variant="standard"
                    value={get(market, 'status', '')}
                    fullWidth
                    disabled
                  />
                </div>
                <div style={styles.rowSpace}>
                  <InputLabel>
                    Link Fleet ID
                  </InputLabel>
                  <TextField
                    autoFocus
                    margin="dense"
                    type="text"
                    variant="standard"
                    value={get(market, 'link.fleetIds[0]', '')}
                    fullWidth
                    disabled
                  />
                </div>
              </Grid>
            </Grid>
          </div>
        )}
        {showTargetDeploymentTable && (
          <TargetDeploymentContainer
            handleBackClick={() => this.setState({
              showTargetDeploymentTable: false,
            })}
          />
        )}
        {showFeatureFlagsModal && editFeatureFlags && (
          <FeatureFlagsModal
            open={showFeatureFlagsModal}
            handleSave={this.handleSave}
            handleClose={this.handleClose}
            flags={editFeatureFlags}
            market={selectedMarket}
            showErrorSnackbar={showErrorSnackbar}
          />
        )}
      </Paper>
    );
  }
}

const mapStateToProps = ({ markets, featureFlags }) => ({
  selectedMarket: markets.selectedMarket,
  markets: markets.markets,
  featureFlags: featureFlags.featureFlags,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
  ...marketActions,
  ...snackbarActions,
  ...featureFlagsActions,
}, dispatch);

export default withPersistentSettings(connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(MarketEditPage)));
